/**
 * Tennis ITF Match Engine (Casual Arena).
 *
 * Hierarchy: Point → Game → Set → Match (independent levels).
 * Persist: points 0–3 · games · sets · server · receiver · serveCourt · orders · ends.
 * Derive: 0/15/30/40/Ad · L/R court layout (ADR-033 — never persist player L/R).
 *
 * scoringSystem: "itf"
 */

import {
  wrapSerializedMatch,
  unwrapSerializedMatch,
} from "../../contracts/MatchEngine";
import { playersFromTeam, setsToWinToBestOf } from "../../../matchScoringBridge";
import { cloneMatchSnapshot } from "../../shared/cloneMatchSnapshot";
import {
  DEFAULT_TENNIS_RULES,
  pointCall,
  otherSide,
  toggleCourt,
  shouldChangeEndsAfterGame,
  shouldChangeEndsAfterSet,
  shouldChangeEndsInTiebreak,
  shouldChangeTbServer,
  gameWinnerFromPoints,
  setWinnerFromGames,
  tiebreakWinner,
  isDecidingSet,
  serveRotation,
  partnerIndex,
  deriveTennisCourtLayout,
  nextPointWinsTiebreak,
} from "./tnRules";

export const TENNIS_ENGINE_ID = "tennis.itf.v1";
export const TENNIS_SCORING_SYSTEM = "itf";
const SCHEMA_VERSION = 2;

export const TENNIS_FAULT_TYPES = Object.freeze([
  { id: "first_serve_fault", label: "First Serve Fault" },
  { id: "double_fault", label: "Double Fault" },
  { id: "serve_let", label: "Serve Let" },
  { id: "net", label: "Net" },
  { id: "out", label: "Out" },
  { id: "foot_fault", label: "Foot Fault" },
  { id: "player_touches_net", label: "Player Touches Net" },
  { id: "double_bounce", label: "Double Bounce" },
  { id: "player_hit", label: "Player Hit" },
  { id: "other", label: "Other" },
]);

/** @typedef {import("./tnRules").DEFAULT_TENNIS_RULES} TennisRulesConfig */

function clonePlayers(list) {
  return (list || []).map((p) => ({ ...p }));
}

function pushUndo(match, snapshot) {
  return {
    ...match,
    undoStack: [...(match.undoStack || []), snapshot].slice(-50),
  };
}

function playersForSide(match, side) {
  return side === "A" ? match.sideAPlayers || [] : match.sideBPlayers || [];
}

function deuceReceiverIndex(match, side) {
  return side === "A"
    ? match.receiveDeuceIndexA ?? 0
    : match.receiveDeuceIndexB ?? 0;
}

function resolveServerId(match, servingSide, serverIndex) {
  const players = playersForSide(match, servingSide);
  if (!players.length) return null;
  if (match.mode !== "doubles") return players[0]?.id || null;
  const idx = Math.max(0, Math.min(serverIndex || 0, players.length - 1));
  return players[idx]?.id || players[0]?.id || null;
}

function resolveReceiverId(match, servingSide, serveCourt) {
  const recvSide = otherSide(servingSide);
  const players = playersForSide(match, recvSide);
  if (!players.length) return null;
  if (match.mode !== "doubles") return players[0]?.id || null;
  const deuceIdx = deuceReceiverIndex(match, recvSide);
  const idx = serveCourt === "left" ? partnerIndex(deuceIdx) : deuceIdx;
  return players[Math.max(0, Math.min(idx, players.length - 1))]?.id || null;
}

/** Court UI: slots[0]=right/deuce, slots[1]=left/ad. */
function reorderTeamByIds(team, rightId, leftId) {
  if (!team || !rightId || !leftId || !team.slots || team.slots.length < 2) {
    return team;
  }
  const byId = new Map();
  for (const slot of team.slots) {
    if (slot.registryEntryId) byId.set(slot.registryEntryId, slot);
    if (slot.playerId) byId.set(slot.playerId, slot);
  }
  const right = byId.get(rightId);
  const left = byId.get(leftId);
  if (!right || !left) return team;
  if (team.slots[0] === right && team.slots[1] === left) return team;
  return { ...team, slots: [right, left] };
}

function courtLayoutForMatch(match) {
  const g = match?.activeSet?.game;
  if (!g) return null;
  return deriveTennisCourtLayout({
    mode: match.mode,
    serveCourt: g.serveCourt || "right",
    servingSide: g.servingSide || "A",
    serverId: g.serverId,
    sideAPlayers: match.sideAPlayers,
    sideBPlayers: match.sideBPlayers,
    receiveDeuceIndexA: match.receiveDeuceIndexA,
    receiveDeuceIndexB: match.receiveDeuceIndexB,
  });
}

/**
 * Tennis doubles court sync (ADR-033 derived L/R — unlike pickleball persisted L/R):
 * - Receiving team: fixed deuce=right, ad=left for the set
 * - Serving team: server occupies serveCourt this point; partner takes the other
 */
function syncTennisTeamsToBoxes(teamA, teamB, match) {
  if (!match || match.mode !== "doubles") return { teamA, teamB };
  const layout = courtLayoutForMatch(match);
  if (!layout) return { teamA, teamB };
  return {
    teamA: reorderTeamByIds(
      teamA,
      layout.sideA.rightPlayerId,
      layout.sideA.leftPlayerId
    ),
    teamB: reorderTeamByIds(
      teamB,
      layout.sideB.rightPlayerId,
      layout.sideB.leftPlayerId
    ),
  };
}

function decorateGame(match, game) {
  const servingSide = game.servingSide || "A";
  const serveCourt = game.serveCourt || "right";
  const serverIndex = game.serverIndex || 0;
  return {
    ...game,
    servingSide,
    serveCourt,
    serverIndex,
    serverId: resolveServerId(match, servingSide, serverIndex),
    receiverId: resolveReceiverId(match, servingSide, serveCourt),
  };
}

function emptyGame(match, servingSide, serverIndex, extras = {}) {
  return decorateGame(match, {
    pointsA: 0,
    pointsB: 0,
    servingSide,
    serverIndex: serverIndex || 0,
    serveCourt: "right",
    serveAttempt: 1,
    isTiebreak: false,
    isMatchTiebreak: false,
    ...extras,
  });
}

function emptySet(rules, firstServerSide, serviceRotationIndex) {
  return {
    gamesA: 0,
    gamesB: 0,
    rules: { ...DEFAULT_TENNIS_RULES, ...rules },
    firstServerSide: firstServerSide || "A",
    serviceRotationIndex: serviceRotationIndex || 0,
    game: null,
    setWinner: null,
  };
}

function setsWon(match, side) {
  return (match.sets || []).filter((s) => s.setWinner === side).length;
}

function matchWinnerFrom(match) {
  const need = Math.ceil((match.bestOf || 3) / 2);
  if (setsWon(match, "A") >= need) return "A";
  if (setsWon(match, "B") >= need) return "B";
  return null;
}

function nextServiceAfterGame(match, set) {
  if (match.mode === "doubles") {
    const nextIdx = ((set.serviceRotationIndex || 0) + 1) % 4;
    const entry = serveRotation(match)[nextIdx];
    return {
      serviceRotationIndex: nextIdx,
      servingSide: entry.side,
      serverIndex: entry.playerIndex,
    };
  }
  return {
    serviceRotationIndex: 0,
    servingSide: otherSide(set.game.servingSide),
    serverIndex: 0,
  };
}

function resolveDoublesRotationIndex(match, servingSide) {
  const rot = serveRotation(match);
  const firstIdx =
    servingSide === "A"
      ? match.serveFirstIndexA ?? 0
      : match.serveFirstIndexB ?? 0;
  return Math.max(
    0,
    rot.findIndex(
      (e) => e.side === servingSide && e.playerIndex === firstIdx
    )
  );
}

function startMatchTiebreakGame(match, servingSide, serverIndex) {
  const to = match.rules?.matchTiebreakTo ?? 10;
  return decorateGame(match, {
    ...emptyGame(match, servingSide, serverIndex),
    isTiebreak: true,
    isMatchTiebreak: true,
    tbPointsA: 0,
    tbPointsB: 0,
    tbServeCount: 0,
    tiebreakTo: to,
  });
}

/**
 * @param {object} match
 * @param {object} finishedSet
 * @param {{ tbEndedWithSixPointChange?: boolean }} [opts]
 */
function completeSet(match, finishedSet, opts = {}) {
  const sets = [
    ...(match.sets || []),
    {
      scoreA: finishedSet.gamesA,
      scoreB: finishedSet.gamesB,
      winner: finishedSet.setWinner,
      setWinner: finishedSet.setWinner,
      firstServerSide: finishedSet.firstServerSide,
      matchTiebreak: !!finishedSet.gameWasMatchTiebreak,
    },
  ];
  const provisional = { ...match, sets, activeSet: null };
  const winner = matchWinnerFrom({ ...provisional, bestOf: match.bestOf });

  // ITF end-of-set ends: flip only when total games odd.
  // If TB already flipped on a completing point that was a multiple of 6,
  // that flip satisfies the odd-set change — do not flip again.
  let endsSwapped = match.endsSwapped;
  const setSaysChange = shouldChangeEndsAfterSet(
    finishedSet.gamesA,
    finishedSet.gamesB
  );
  if (setSaysChange && !opts.tbEndedWithSixPointChange) {
    endsSwapped = !endsSwapped;
  } else if (!setSaysChange && opts.tbEndedWithSixPointChange) {
    // Even-game set cannot happen after TB (always 7–6), but keep safe: undo mid-TB flip.
    endsSwapped = !endsSwapped;
  }

  if (winner) {
    return {
      ...provisional,
      matchWinner: winner,
      awaitingNextSet: false,
      endsSwapped,
    };
  }

  // Next set: first server = who would have received first in the finished set
  const nextServe = otherSide(finishedSet.firstServerSide || "A");
  const nextMatch = {
    ...provisional,
    matchWinner: null,
    awaitingNextSet: false,
    endsSwapped,
  };

  let serviceRotationIndex = 0;
  let serverIndex = 0;
  if (match.mode === "doubles") {
    serviceRotationIndex = resolveDoublesRotationIndex(nextMatch, nextServe);
    serverIndex =
      serveRotation(nextMatch)[serviceRotationIndex]?.playerIndex || 0;
  }

  const entry =
    match.mode === "doubles"
      ? serveRotation(nextMatch)[serviceRotationIndex]
      : { side: nextServe, playerIndex: 0 };

  // Deciding set as Match Tiebreak (10-pt) when configured
  const provisionalNext = {
    ...nextMatch,
    sets, // already on nextMatch
  };
  const deciding =
    match.rules?.matchTiebreak &&
    isDecidingSet({ ...provisionalNext, bestOf: match.bestOf });

  const setRules = { ...match.rules };
  const activeSet = {
    ...emptySet(setRules, nextServe, serviceRotationIndex),
    game: deciding
      ? startMatchTiebreakGame(nextMatch, entry.side, entry.playerIndex)
      : emptyGame(nextMatch, entry.side, entry.playerIndex),
  };

  return { ...nextMatch, activeSet };
}

function awardGame(match, set, winnerSide) {
  const gamesA = winnerSide === "A" ? set.gamesA + 1 : set.gamesA;
  const gamesB = winnerSide === "B" ? set.gamesB + 1 : set.gamesB;
  const gameSum = gamesA + gamesB;
  const serve = nextServiceAfterGame(match, set);
  const rules = { ...set.rules };
  const setWinner = setWinnerFromGames(gamesA, gamesB, rules);

  // Set-ending game: apply end-of-set ends only (not mid-set odd-game + set-end).
  if (setWinner) {
    const finished = {
      ...set,
      gamesA,
      gamesB,
      serviceRotationIndex: serve.serviceRotationIndex,
      setWinner,
      game: null,
      gameWasMatchTiebreak: false,
    };
    return completeSet(match, finished);
  }

  // Continuing set (or entering TB): odd completed games change ends
  const nextMatch = {
    ...match,
    endsSwapped: shouldChangeEndsAfterGame(gameSum)
      ? !match.endsSwapped
      : match.endsSwapped,
  };

  let nextSet = {
    ...set,
    gamesA,
    gamesB,
    serviceRotationIndex: serve.serviceRotationIndex,
    game: emptyGame(nextMatch, serve.servingSide, serve.serverIndex),
  };

  // Tiebreak at N-N — next server in rotation starts TB (one point, then alternate by 2)
  if (
    !set.game.isTiebreak &&
    gamesA === rules.tiebreakAt &&
    gamesB === rules.tiebreakAt
  ) {
    nextSet.game = decorateGame(nextMatch, {
      ...emptyGame(nextMatch, serve.servingSide, serve.serverIndex),
      isTiebreak: true,
      isMatchTiebreak: false,
      tbPointsA: 0,
      tbPointsB: 0,
      tbServeCount: 0,
      tiebreakTo: rules.tiebreakTo || 7,
    });
  }

  return { ...nextMatch, activeSet: nextSet };
}

function awardTiebreakPoint(match, set, side) {
  const g = set.game;
  const to =
    g.tiebreakTo ||
    (g.isMatchTiebreak
      ? match.rules?.matchTiebreakTo || 10
      : set.rules.tiebreakTo || 7);

  let nextGame = {
    ...g,
    tbPointsA: g.tbPointsA + (side === "A" ? 1 : 0),
    tbPointsB: g.tbPointsB + (side === "B" ? 1 : 0),
    tbServeCount: (g.tbServeCount || 0) + 1,
    serveAttempt: 1,
    serveCourt: toggleCourt(g.serveCourt || "right"),
    tiebreakTo: to,
  };
  const a = nextGame.tbPointsA;
  const b = nextGame.tbPointsB;
  const count = nextGame.tbServeCount;

  let servingSide = g.servingSide;
  let serverIndex = g.serverIndex || 0;
  let serviceRotationIndex = set.serviceRotationIndex || 0;
  if (shouldChangeTbServer(count)) {
    if (match.mode === "doubles") {
      serviceRotationIndex = (serviceRotationIndex + 1) % 4;
      const entry = serveRotation(match)[serviceRotationIndex];
      servingSide = entry.side;
      serverIndex = entry.playerIndex;
    } else {
      servingSide = otherSide(servingSide);
    }
  }
  nextGame = decorateGame(match, {
    ...nextGame,
    servingSide,
    serverIndex,
  });

  let endsSwapped = match.endsSwapped;
  const sixPointChange = shouldChangeEndsInTiebreak(count);
  if (sixPointChange) {
    endsSwapped = !endsSwapped;
  }

  const winner = tiebreakWinner(a, b, to);
  if (winner) {
    const finished = {
      ...set,
      serviceRotationIndex,
      gamesA: g.isMatchTiebreak
        ? winner === "A"
          ? 1
          : 0
        : set.gamesA + (winner === "A" ? 1 : 0),
      gamesB: g.isMatchTiebreak
        ? winner === "B"
          ? 1
          : 0
        : set.gamesB + (winner === "B" ? 1 : 0),
      setWinner: winner,
      game: null,
      gameWasMatchTiebreak: !!g.isMatchTiebreak,
    };
    // Match TB: treat as 1–0 games (odd) for end-of-set ends rule
    return completeSet({ ...match, endsSwapped }, finished, {
      tbEndedWithSixPointChange: sixPointChange,
    });
  }

  return {
    ...match,
    endsSwapped,
    activeSet: {
      ...set,
      serviceRotationIndex,
      game: nextGame,
    },
  };
}

function awardPoint(match, side) {
  const set = match.activeSet;
  if (!set?.game || set.setWinner) return match;
  if (set.game.isTiebreak) {
    return awardTiebreakPoint(match, set, side);
  }
  const g = set.game;
  const nextPoints = {
    pointsA: g.pointsA + (side === "A" ? 1 : 0),
    pointsB: g.pointsB + (side === "B" ? 1 : 0),
  };
  const gameWinner = gameWinnerFromPoints(
    nextPoints.pointsA,
    nextPoints.pointsB,
    set.rules.noAd
  );
  if (gameWinner) {
    return awardGame(
      match,
      { ...set, game: { ...g, ...nextPoints, serveAttempt: 1 } },
      gameWinner
    );
  }
  // Same server; alternate deuce/ad court after every point
  const serveCourt = toggleCourt(g.serveCourt || "right");
  return {
    ...match,
    activeSet: {
      ...set,
      game: decorateGame(match, {
        ...g,
        ...nextPoints,
        serveCourt,
        serveAttempt: 1,
      }),
    },
  };
}

/**
 * @returns {import("../../contracts/MatchEngine").MatchEngine}
 */
export function createTennisMatchEngine(rulesOverride = {}) {
  const rules = { ...DEFAULT_TENNIS_RULES, ...rulesOverride };

  const engine = {
    engineId: TENNIS_ENGINE_ID,
    scoringSystem: TENNIS_SCORING_SYSTEM,

    startMatch(config) {
      const mode = config.teamA?.type === "doubles" ? "doubles" : "singles";
      const servingSide = config.firstServerSide || "A";
      const gamesToWin = Math.max(
        4,
        Math.min(12, Number(config.pointsToWin ?? rules.gamesToWin ?? 6) || 6)
      );
      const matchRules = {
        ...rules,
        gamesToWin,
        winByGames: rules.winByGames ?? 2,
        tiebreakAt: gamesToWin,
        tiebreakTo: rules.tiebreakTo ?? 7,
        noAd: !!config.noAd || !!rules.noAd,
        matchTiebreak:
          config.matchTiebreak != null
            ? !!config.matchTiebreak
            : !!rules.matchTiebreak,
        matchTiebreakTo: Math.max(
          7,
          Number(
            config.matchTiebreakTo ?? rules.matchTiebreakTo ?? 10
          ) || 10
        ),
      };
      const sideAPlayers = clonePlayers(playersFromTeam(config.teamA));
      const sideBPlayers = clonePlayers(playersFromTeam(config.teamB));
      const serveFirstIndexA = Math.max(
        0,
        Math.min(
          Number(config.serveFirstIndexA ?? 0) || 0,
          Math.max(0, sideAPlayers.length - 1)
        )
      );
      const serveFirstIndexB = Math.max(
        0,
        Math.min(
          Number(config.serveFirstIndexB ?? 0) || 0,
          Math.max(0, sideBPlayers.length - 1)
        )
      );
      const receiveDeuceIndexA = Math.max(
        0,
        Math.min(
          Number(config.receiveDeuceIndexA ?? 0) || 0,
          Math.max(0, sideAPlayers.length - 1)
        )
      );
      const receiveDeuceIndexB = Math.max(
        0,
        Math.min(
          Number(config.receiveDeuceIndexB ?? 0) || 0,
          Math.max(0, sideBPlayers.length - 1)
        )
      );

      const provisional = {
        engineId: TENNIS_ENGINE_ID,
        scoringSystem: TENNIS_SCORING_SYSTEM,
        bestOf: setsToWinToBestOf(config.setsToWin ?? 2),
        rules: matchRules,
        mode,
        endsSwapped: !!config.endsSwapped,
        sideAPlayers,
        sideBPlayers,
        serveFirstIndexA,
        serveFirstIndexB,
        receiveDeuceIndexA,
        receiveDeuceIndexB,
        sets: [],
        matchWinner: null,
        undoStack: [],
      };

      let serviceRotationIndex = 0;
      let serverIndex = 0;
      if (mode === "doubles") {
        serviceRotationIndex = resolveDoublesRotationIndex(
          provisional,
          servingSide
        );
        serverIndex =
          serveRotation(provisional)[serviceRotationIndex]?.playerIndex || 0;
      }

      // Best-of-1 with match TB: open as Match Tiebreak
      const openAsMatchTb =
        matchRules.matchTiebreak &&
        Math.ceil((provisional.bestOf || 3) / 2) === 1;

      return {
        ...provisional,
        activeSet: {
          ...emptySet(matchRules, servingSide, serviceRotationIndex),
          game: openAsMatchTb
            ? startMatchTiebreakGame(provisional, servingSide, serverIndex)
            : emptyGame(provisional, servingSide, serverIndex),
        },
      };
    },

    startNextGame(match, config) {
      return engine.startMatch(config);
    },

    scorePoint(match, side) {
      if (!match?.activeSet?.game || match.matchWinner) return match;
      const snapshot = cloneMatchSnapshot(match);
      return pushUndo(awardPoint(match, side), snapshot);
    },

    fault(match, faultType, side) {
      if (!match?.activeSet?.game || match.matchWinner) return match;
      const snapshot = cloneMatchSnapshot(match);
      const set = match.activeSet;
      const g = set.game;
      const type = faultType || "other";

      if (type === "serve_let") {
        return match;
      }

      if (type === "first_serve_fault") {
        if (g.serveAttempt === 1) {
          return pushUndo(
            {
              ...match,
              activeSet: {
                ...set,
                game: { ...g, serveAttempt: 2 },
              },
            },
            snapshot
          );
        }
        return pushUndo(awardPoint(match, otherSide(g.servingSide)), snapshot);
      }

      if (type === "double_fault") {
        return pushUndo(awardPoint(match, otherSide(g.servingSide)), snapshot);
      }

      const faulting = side || g.servingSide;
      return pushUndo(awardPoint(match, otherSide(faulting)), snapshot);
    },

    undo(match) {
      if (!match?.undoStack?.length) return match;
      return match.undoStack[match.undoStack.length - 1];
    },

    canUndo(match) {
      return Boolean(match?.undoStack?.length);
    },

    getState(match) {
      return match || null;
    },

    getLiveView(match) {
      if (!match) {
        return {
          scoreA: "0",
          scoreB: "0",
          serving: "A",
          sets: [],
          winner: null,
          endsSwapped: false,
          serveDisplay: null,
          extras: null,
        };
      }
      const set = match.activeSet;
      const g = set?.game;
      const setIndex = (match.sets || []).length + 1;
      const courtLabel =
        g?.serveCourt === "left" ? "Ad court" : "Deuce court";
      const layout = courtLayoutForMatch(match);

      if (g?.isTiebreak) {
        const phase = g.isMatchTiebreak ? "MATCH TIEBREAK" : "TIEBREAK";
        return {
          scoreA: g.tbPointsA,
          scoreB: g.tbPointsB,
          serving: g.servingSide || "A",
          sets: (match.sets || []).map((s) => ({
            scoreA: s.scoreA,
            scoreB: s.scoreB,
            winner: s.winner || s.setWinner,
          })),
          winner: match.matchWinner,
          endsSwapped: !!match.endsSwapped,
          serveDisplay: `${g.isMatchTiebreak ? "MTB" : "TB"} ${g.tbPointsA}-${g.tbPointsB} · ${courtLabel} · ${g.serveAttempt === 2 ? "2nd" : "1st"} serve`,
          extras: {
            gamesA: set.gamesA,
            gamesB: set.gamesB,
            serveAttempt: g.serveAttempt,
            serveCourt: g.serveCourt,
            serverId: g.serverId,
            receiverId: g.receiverId,
            isTiebreak: true,
            isMatchTiebreak: !!g.isMatchTiebreak,
            deuce: false,
            phaseLabel: phase,
            courtLayout: layout,
            scoreMeta: [
              g.isMatchTiebreak
                ? `Match TB to ${g.tiebreakTo || match.rules?.matchTiebreakTo || 10}`
                : `Set games ${set.gamesA}–${set.gamesB}`,
              courtLabel,
              `${g.serveAttempt === 2 ? "2nd" : "1st"} serve`,
            ]
              .filter(Boolean)
              .join(" · "),
          },
        };
      }

      if (g) {
        const scoreA = pointCall(g.pointsA, g.pointsB);
        const scoreB = pointCall(g.pointsB, g.pointsA);
        const deuce =
          g.pointsA >= 3 && g.pointsB >= 3 && g.pointsA === g.pointsB;
        const ad =
          g.pointsA >= 3 && g.pointsB >= 3 && g.pointsA !== g.pointsB
            ? "Advantage"
            : null;
        return {
          scoreA,
          scoreB,
          serving: g.servingSide || "A",
          sets: (match.sets || []).map((s) => ({
            scoreA: s.scoreA,
            scoreB: s.scoreB,
            winner: s.winner || s.setWinner,
          })),
          winner: match.matchWinner,
          endsSwapped: !!match.endsSwapped,
          serveDisplay: `Games ${set.gamesA}–${set.gamesB} · ${scoreA}–${scoreB} · ${courtLabel}`,
          extras: {
            gamesA: set.gamesA,
            gamesB: set.gamesB,
            serveAttempt: g.serveAttempt,
            serveCourt: g.serveCourt,
            serverId: g.serverId,
            receiverId: g.receiverId,
            isTiebreak: false,
            isMatchTiebreak: false,
            deuce,
            phaseLabel: `SET ${setIndex}`,
            courtLayout: layout,
            scoreMeta: [
              `Games ${set.gamesA}–${set.gamesB}`,
              deuce ? "Deuce" : ad,
              courtLabel,
              `${g.serveAttempt === 2 ? "2nd" : "1st"} serve`,
            ]
              .filter(Boolean)
              .join(" · "),
          },
        };
      }

      return {
        scoreA: 0,
        scoreB: 0,
        serving: "A",
        sets: (match.sets || []).map((s) => ({
          scoreA: s.scoreA,
          scoreB: s.scoreB,
          winner: s.winner || s.setWinner,
        })),
        winner: match.matchWinner,
        endsSwapped: !!match.endsSwapped,
        serveDisplay: null,
        extras: set
          ? {
              gamesA: set.gamesA,
              gamesB: set.gamesB,
              isTiebreak: false,
              deuce: false,
              phaseLabel: `SET ${setIndex}`,
              scoreMeta: null,
              courtLayout: null,
            }
          : null,
      };
    },

    getWinner(match) {
      return match?.matchWinner ?? null;
    },

    serialize(match) {
      if (!match) return null;
      return wrapSerializedMatch(TENNIS_ENGINE_ID, SCHEMA_VERSION, match);
    },

    deserialize(json) {
      const unwrapped = unwrapSerializedMatch(json);
      if (!unwrapped.state || unwrapped.legacy) return null;
      if (unwrapped.engineId && unwrapped.engineId !== TENNIS_ENGINE_ID)
        return null;
      return unwrapped.state;
    },

    isGamePoint(match) {
      const g = match?.activeSet?.game;
      if (!g) return false;
      if (g.isTiebreak) {
        const to =
          g.tiebreakTo ||
          (g.isMatchTiebreak
            ? match.rules?.matchTiebreakTo || 10
            : match.activeSet.rules?.tiebreakTo || 7);
        return (
          nextPointWinsTiebreak(g.tbPointsA, g.tbPointsB, to) ||
          nextPointWinsTiebreak(g.tbPointsB, g.tbPointsA, to)
        );
      }
      const check = (pa, pb) => pa >= 3 && pa - pb >= 1;
      return check(g.pointsA, g.pointsB) || check(g.pointsB, g.pointsA);
    },

    isMatchPoint(match) {
      const need = Math.ceil((match.bestOf || 3) / 2);
      const set = match.activeSet;
      const g = set?.game;
      if (!g || !set) return false;

      if (g.isTiebreak) {
        const to =
          g.tiebreakTo ||
          (g.isMatchTiebreak
            ? match.rules?.matchTiebreakTo || 10
            : set.rules?.tiebreakTo || 7);
        let side = null;
        if (nextPointWinsTiebreak(g.tbPointsA, g.tbPointsB, to)) side = "A";
        else if (nextPointWinsTiebreak(g.tbPointsB, g.tbPointsA, to))
          side = "B";
        if (!side) return false;
        return setsWon(match, side) + 1 >= need;
      }

      if (!this.isGamePoint(match)) return false;
      const sideAtGamePoint =
        g.pointsA > g.pointsB ? "A" : g.pointsB > g.pointsA ? "B" : null;
      if (!sideAtGamePoint) return false;
      const gamesSide =
        sideAtGamePoint === "A" ? set.gamesA : set.gamesB;
      const gamesOpp =
        sideAtGamePoint === "A" ? set.gamesB : set.gamesA;
      const setPoint =
        gamesSide >= (set.rules.gamesToWin || 6) - 1 &&
        gamesSide - gamesOpp >= 1;
      return setPoint && setsWon(match, sideAtGamePoint) + 1 >= need;
    },

    hasInterval() {
      return false;
    },

    resumeInterval(match) {
      return match;
    },

    isAwaitingCourtSetup() {
      return false;
    },

    suggestCourtSetup(match) {
      return {
        suggestedServerSide: match?.matchWinner === "A" ? "B" : "A",
        endsSwapped: !match?.endsSwapped,
      };
    },

    syncTeamsToBoxes(teamA, teamB, match) {
      return syncTennisTeamsToBoxes(teamA, teamB, match);
    },

    listFaultTypes() {
      return [...TENNIS_FAULT_TYPES];
    },

    setsToWinToBestOf,
  };

  return {
    ...engine,
    initScoringMatch(
      teamA,
      teamB,
      setsToWin,
      endsSwapped,
      firstServerSide,
      pointsToWin,
      extras = {}
    ) {
      return engine.startMatch({
        teamA,
        teamB,
        setsToWin,
        endsSwapped,
        firstServerSide,
        pointsToWin,
        ...extras,
      });
    },
    startNextGameFromCourtSetup(
      existing,
      teamA,
      teamB,
      endsSwapped,
      firstServerSide,
      pointsToWin
    ) {
      return engine.startNextGame(existing, {
        teamA,
        teamB,
        endsSwapped,
        firstServerSide,
        pointsToWin,
      });
    },
    matchToLiveView: engine.getLiveView,
    serializeScoringMatch: engine.serialize,
    deserializeScoringMatch: engine.deserialize,
    recordRally: engine.scorePoint,
    undoLastRally: engine.undo,
    canUndoRally: engine.canUndo,
    resumeFromInterval: engine.resumeInterval,
    suggestCourtSetupAfterGame: engine.suggestCourtSetup,
    syncTeamsToActiveGameBoxes: engine.syncTeamsToBoxes,
  };
}
